import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Trash2, ArrowRightLeft } from "lucide-react";
import { endpoints, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { toast } from "sonner";

const KIND_LABEL = {
    artist: "Artist",
    venue: "Venue",
    promoter: "Promoter",
    label: "Label",
};

/**
 * Danger Zone — user-facing self-delete.
 *
 * Blocks self-delete when the account is the sole Owner of any
 * workspace. In that case we surface the affected workspaces AND
 * expose an inline transfer form so the user can hand ownership off
 * to another Project Mint account without leaving the page. Once
 * every workspace is transferred, the destructive confirmation input
 * unlocks.
 */
export default function SelfDeleteAccountSection() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmation, setConfirmation] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [transferState, setTransferState] = useState({});

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const d = await endpoints.accountDeletionPreview();
            setPreview(d);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (expanded && !preview) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const soleOwner = preview?.sole_owner_workspaces || [];
    const canDelete = soleOwner.length === 0 && confirmation.trim() === "DELETE";

    const runTransfer = async (w) => {
        const st = transferState[w.id] || {};
        const email = (st.email || "").trim();
        if (!email) {
            toast.error("Enter the new owner's Project Mint email");
            return;
        }
        setTransferState((s) => ({ ...s, [w.id]: { ...st, busy: true } }));
        try {
            await endpoints.accountTransferOwnership({
                kind: w.kind,
                org_id: w.id,
                new_owner_email: email,
            });
            toast.success(`${KIND_LABEL[w.kind]} ownership transferred`);
            setTransferState((s) => ({ ...s, [w.id]: { email: "", busy: false } }));
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
            setTransferState((s) => ({ ...s, [w.id]: { ...st, busy: false } }));
        }
    };

    const doDelete = async () => {
        if (!canDelete) return;
        setBusy(true);
        setError("");
        try {
            await endpoints.accountSelfDelete({ confirmation: "DELETE" });
            toast.success("Account deleted");
            // Force local logout & redirect. Server has already cleared cookies.
            try { await logout(); } catch {}
            nav("/", { replace: true });
        } catch (err) {
            setError(formatApiError(err));
            setBusy(false);
        }
    };

    return (
        <section className="mt-6 border border-[#ff3333]/60 p-6" data-testid="self-delete-section">
            <div className="flex items-center gap-2 text-[#ff3333]">
                <AlertTriangle className="w-4 h-4" />
                <h2 className="font-display font-black uppercase text-lg">Danger zone</h2>
            </div>
            <p className="mt-2 text-xs font-mono uppercase text-[#a3a3a3]">
                Permanently delete your Project Mint account and personal relationships. Followed artists, venues, and events themselves are preserved.
            </p>

            {!expanded ? (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="pm-btn text-xs mt-4 border-[#ff3333] text-[#ff3333]"
                    data-testid="self-delete-expand-btn"
                >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete Project Mint account
                </button>
            ) : (
                <div className="mt-4 space-y-4">
                    {loading && (
                        <div className="text-xs font-mono uppercase text-[#666]" data-testid="self-delete-loading">Loading…</div>
                    )}

                    {!loading && preview && (
                        <>
                            <div className="border border-[#2a2a2a] p-3 text-[11px] font-mono uppercase text-[#a3a3a3]" data-testid="self-delete-impact">
                                Will remove: <span className="text-[#f4f4f0]">{preview.impact?.registrations || 0}</span> registrations,{" "}
                                <span className="text-[#f4f4f0]">{preview.impact?.artist_follows || 0}</span> artist follows,{" "}
                                <span className="text-[#f4f4f0]">{preview.impact?.venue_follows || 0}</span> venue follows,{" "}
                                <span className="text-[#f4f4f0]">{preview.memberships?.length || 0}</span> workspace memberships.
                            </div>

                            {soleOwner.length > 0 && (
                                <div className="border border-[#ff9500] p-3" data-testid="self-delete-blocked">
                                    <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-[#ff9500]">
                                        <AlertTriangle className="w-3 h-3" /> Transfer or remove first
                                    </div>
                                    <p className="mt-2 text-[11px] text-[#f4f4f0]">
                                        You are the sole Owner of the workspaces below. Transfer ownership to another Project Mint account before deleting your account — this prevents Artists, Venues, Promoters, or Labels from being orphaned.
                                    </p>
                                    <ul className="mt-3 space-y-2">
                                        {soleOwner.map((w) => {
                                            const st = transferState[w.id] || {};
                                            return (
                                                <li key={w.id} className="border border-[#2a2a2a] p-2" data-testid={`self-delete-owner-${w.id}`}>
                                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <div className="text-sm font-display font-bold uppercase truncate">{w.name}</div>
                                                            <div className="text-[10px] font-mono uppercase text-[#666]">{KIND_LABEL[w.kind]}</div>
                                                        </div>
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                                        <div className="flex items-center gap-1 text-[10px] font-mono uppercase text-[#666] mr-1">
                                                            <ArrowRightLeft className="w-3 h-3" /> Transfer to
                                                        </div>
                                                        <input
                                                            type="email"
                                                            placeholder="new-owner@email"
                                                            value={st.email || ""}
                                                            onChange={(e) => setTransferState((s) => ({ ...s, [w.id]: { ...st, email: e.target.value } }))}
                                                            className="flex-1 min-w-[200px] bg-transparent border border-[#2a2a2a] px-2 py-1 text-[11px] font-mono"
                                                            data-testid={`self-transfer-email-${w.id}`}
                                                        />
                                                        <button
                                                            type="button"
                                                            disabled={st.busy}
                                                            onClick={() => runTransfer(w)}
                                                            className="pm-btn text-[10px]"
                                                            data-testid={`self-transfer-submit-${w.id}`}
                                                        >
                                                            {st.busy ? "Transferring…" : "Transfer"}
                                                        </button>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}

                            <div>
                                <label className="text-[10px] font-mono uppercase text-[#a3a3a3]">
                                    Type <span className="text-[#ff3333]">DELETE</span> to confirm
                                </label>
                                <input
                                    type="text"
                                    value={confirmation}
                                    onChange={(e) => setConfirmation(e.target.value)}
                                    disabled={soleOwner.length > 0}
                                    className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff3333] py-2 outline-none font-mono text-sm disabled:opacity-40"
                                    data-testid="self-delete-confirm-input"
                                    placeholder="DELETE"
                                    autoComplete="off"
                                />
                            </div>

                            {error && (
                                <div className="text-xs font-mono uppercase text-[#ff3333]" data-testid="self-delete-error">
                                    {error}
                                </div>
                            )}

                            <div className="flex items-center justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setExpanded(false); setConfirmation(""); setError(""); }}
                                    className="pm-btn text-xs"
                                    data-testid="self-delete-cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={doDelete}
                                    disabled={!canDelete || busy}
                                    className="pm-btn text-xs bg-[#ff3333] text-[#0a0a0a] border-[#ff3333] disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="self-delete-confirm-btn"
                                >
                                    <Trash2 className="w-3 h-3 mr-1" /> {busy ? "Deleting…" : "Delete my account"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
